import { RequestService } from '@tcc-online/request-service';
import IAdminCustomer from '../types/IAdminCustomer';
import IAdminUser from '../types/users/IAdminUser';
import UserRoles from '../../common/constants/UserRoles';
import AdminUsersService from './AdminUsersService';

const AdminCustomerService = {
    getCustomer,
    updateCustomer,
    getCustomers,
    createCustomer,
    getCompanyAdmins,
    createCompanyAdmin,
};

const BASE_URL = '/admin/companies';

async function getCustomer(customerId?: number) {
    return await RequestService.get<IAdminCustomer>(
        `${BASE_URL}/${customerId}`,
    );
}

export interface BasePayload {
    street?: string;
    city?: string;
    houseNumber?: string;
    postalCode?: string;
    taxNumber?: string;
    registrationNumber?: string;
    languages?: string[];
}

export interface UpdatePayload extends BasePayload {
    id?: number;
    name?: string;
    primaryAdminId?: number | null;
    settings?: Record<string, any> | null;
}

async function updateCustomer(payload: UpdatePayload) {
    const {
        id,
        name,
        street,
        city,
        houseNumber,
        postalCode,
        taxNumber,
        registrationNumber,
        languages,
        primaryAdminId,
        settings,
    } = payload;
    return await RequestService.patch<
        Omit<UpdatePayload, 'id'>,
        IAdminCustomer
    >(
        `${BASE_URL}/${id}`,
        {
            name,
            street,
            city,
            houseNumber,
            postalCode,
            taxNumber,
            registrationNumber,
            languages,
            primaryAdminId,
            settings,
        },
        { allowMultiRequest: true },
    );
}

async function getCustomers(payload: { page?: number; search?: string }) {
    const { page = 1, search } = payload;
    return await RequestService.get<IAdminCustomer[]>(`${BASE_URL}`, {
        page,
        search,
    });
}

async function createCustomer() {
    return await RequestService.post<Record<string, never>, IAdminCustomer>(
        BASE_URL,
        {},
    );
}

async function getCompanyAdmins(companyId?: number) {
    return await AdminUsersService.getUsers({
        companyId,
        minRole: UserRoles.manager,
        limit: 100,
    });
}

export interface CreateCompanyAdminPayload {
    companyId?: number;
    email: string;
    firstName?: string;
    lastName?: string;
}

async function createCompanyAdmin(payload: CreateCompanyAdminPayload) {
    const { companyId, ...body } = payload;
    return await RequestService.post<typeof body, IAdminUser>(
        `${BASE_URL}/${companyId}/users`,
        body,
    );
}

export default AdminCustomerService;
